import type {
  CommunityPreviewDeviceMeta,
  DeviceCatalogSnapshot,
} from "@eweadn-qiankun/shared-types";
import { getSupportedDevicesSync } from "../main/supportedDevicesStore";
import {
  resolveKeyboardCatalogRowForEquipment,
  resolveMouseCatalogRowForEquipment,
} from "../musicRhythm/supportedKeyboardMatch";

/** 分享/预览用的配置表定位快照（与 connectList.configuration / equipmentData 对齐） */
export type { DeviceCatalogSnapshot };

export function normalizeCommunityBoardProvider(raw?: string): string {
  if (raw === "beiyingMachine") return "beiying";
  return (raw ?? "").toLowerCase();
}

function normalizeUuid(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const nums = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
  return nums.length ? nums : undefined;
}

export function buildDeviceCatalogSnapshot(
  equipment: Record<string, unknown> | null | undefined
): DeviceCatalogSnapshot | null {
  if (!equipment || typeof equipment !== "object") return null;

  const vendorId = Number(equipment.vendorId);
  const productId = Number(equipment.productId);
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) {
    return null;
  }

  const keyboardType = String(equipment.keyboardType ?? "").trim();
  const solutionRovider = String(equipment.solutionRovider ?? "").trim();
  const productName = String(equipment.productName ?? "").trim();
  const displayname = String(
    equipment.displayname ?? equipment.displayName ?? ""
  ).trim();
  const identifier = String(equipment.identifier ?? "").trim();
  const uuid = normalizeUuid(equipment.uuid);
  const deviceId = String(equipment.deviceId ?? "").trim();

  return {
    vendorId,
    productId,
    ...(keyboardType ? { keyboardType } : {}),
    ...(solutionRovider ? { solutionRovider } : {}),
    ...(productName ? { productName } : {}),
    ...(displayname ? { displayname } : {}),
    ...(identifier ? { identifier } : {}),
    ...(uuid ? { uuid } : {}),
    ...(deviceId ? { deviceId } : {}),
  };
}

export function readDeviceCatalogSnapshotFromPayload(
  payload?: Record<string, unknown> | null
): DeviceCatalogSnapshot | null {
  if (!payload || typeof payload !== "object") return null;

  const embedded = payload._deviceCatalog;
  if (embedded && typeof embedded === "object") {
    return buildDeviceCatalogSnapshot(embedded as Record<string, unknown>);
  }

  return buildDeviceCatalogSnapshot(payload);
}

export function resolveCatalogRowFromSnapshot(
  snapshot: DeviceCatalogSnapshot | null | undefined,
  category: "keyboard" | "mouse"
): Record<string, unknown> | null {
  if (!snapshot) return null;
  if (category === "mouse") {
    return resolveMouseCatalogRowForEquipment(snapshot);
  }
  return resolveKeyboardCatalogRowForEquipment(snapshot);
}

function resolveKeyboardCatalogRowByDisplayname(
  displayname: string
): Record<string, unknown> | null {
  const name = displayname.trim();
  if (!name) return null;
  const rows = (
    getSupportedDevicesSync().keyboard as Array<Record<string, unknown>>
  ).filter(
    (row) =>
      row.isSupported !== false &&
      row.communitySupported !== false &&
      String(row.displayname ?? row.productName ?? "").trim() === name
  );
  if (rows.length === 1) return rows[0]!;
  if (rows.length > 1) {
    const wired = rows.filter(
      (row) => row.workMode !== "2" && row.workMode !== 2 && row.is4G !== true
    );
    return wired[0] ?? rows[0]!;
  }
  return null;
}

export function communityPreviewMetaFromCatalogRow(
  displayname: string,
  row: Record<string, unknown>,
  snapshot?: DeviceCatalogSnapshot | null
): CommunityPreviewDeviceMeta {
  return {
    displayname: String(row.displayname ?? displayname),
    keyboardType: String(row.keyboardType ?? "87"),
    mouseDeviceId: String(row.deviceId ?? "z7"),
    solutionProvider:
      normalizeCommunityBoardProvider(String(row.solutionRovider ?? "")) ||
      "xingshan",
    deviceCatalog: snapshot ?? buildDeviceCatalogSnapshot(row),
  };
}

/** 无配置表快照时的兜底：仅用 payload 内方案商/矩阵类型，不按 displayname 查表 */
export function communityPreviewMetaFromPayloadFallback(
  displayname: string,
  payload?: Record<string, unknown> | null
): CommunityPreviewDeviceMeta {
  const snapshot = readDeviceCatalogSnapshotFromPayload(payload);
  const catalogRow =
    resolveCatalogRowFromSnapshot(snapshot, "keyboard") ??
    (snapshot ? resolveKeyboardCatalogRowForEquipment(snapshot) : null) ??
    resolveKeyboardCatalogRowByDisplayname(displayname);

  if (catalogRow) {
    return communityPreviewMetaFromCatalogRow(
      displayname,
      catalogRow,
      snapshot ?? buildDeviceCatalogSnapshot(catalogRow)
    );
  }

  const provider = normalizeCommunityBoardProvider(
    String(payload?._boardProvider ?? "")
  );
  const keyboardType = String(payload?._keyboardType ?? "").trim() || "87";
  return {
    displayname,
    keyboardType,
    mouseDeviceId: "z7",
    solutionProvider: provider || "xingshan",
    ...(snapshot ? { deviceCatalog: snapshot } : {}),
  };
}
